// TwoFactorDialog.tsx — enable/disable TOTP two-factor auth (Settings →
// Privacy "Session security"). The enable flow is modeled EXACTLY on
// ContactVerifyDialog's request|confirm machine (same password re-auth →
// code-confirm shape), extended with a third step for the one-time recovery
// codes. The disable flow mirrors RemoveContactDialog (password + code).
import { useState } from 'react';
import styled from 'styled-components';
import { Dialog, Button, TextField, useToast } from '@/ui';
import { Icon } from '@/icons';
import { authService } from '@/core/services/authService';
import { ApiError } from '@/core/lib/apiClient';
import { downloadRecoveryCodes } from '@/core/lib/downloadRecoveryCodes';
import { twoFactorInputError } from '@/core/lib/twoFactorInput';

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Hint = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.5;
`;

const ErrorText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`;

const QrWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
`;

const QrImage = styled.img`
  width: 180px;
  height: 180px;
`;

const SecretRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SecretLabel = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textMuted};
`;

const SecretText = styled.code`
  padding: 9px 12px;
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  letter-spacing: 0.1em;
  user-select: all;
  word-break: break-all;
`;

const OtpauthLink = styled.a`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.accentCyan};
`;

const RecoveryList = styled.ul`
  margin: 0;
  padding: 12px 14px;
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const RecoveryCode = styled.code`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  letter-spacing: 0.05em;
`;

const WarnHint = styled(Hint)`
  color: ${({ theme }) => theme.color.accentAmber};
`;

// ── Enable ────────────────────────────────────────────────────────────────

export interface TwoFactorEnableDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called once the member closes the recovery-codes step (2FA is now on). */
  onEnabled: () => void;
}

type EnableStep = 'password' | 'qr' | 'recovery';

export function TwoFactorEnableDialog({ open, onClose, onEnabled }: TwoFactorEnableDialogProps) {
  const toast = useToast();

  const [step, setStep] = useState<EnableStep>('password');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [qrSvg, setQrSvg] = useState('');
  const [secret, setSecret] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [accountHandle, setAccountHandle] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function reset() {
    setStep('password');
    setPassword('');
    setCode('');
    setOtpauthUri('');
    setQrSvg('');
    setSecret('');
    setRecoveryCodes([]);
    setAccountHandle('');
    setError('');
  }

  function close() {
    reset();
    onClose();
  }

  async function handlePasswordStep() {
    if (!password) {
      setError('Enter your password to continue.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await authService.setupTwoFactor(password);
      setOtpauthUri(r.otpauthUri);
      setQrSvg(r.qrSvg);
      setSecret(r.secret);
      setAccountHandle(r.accountHandle ?? '');
      setStep('qr');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'wrong_password') {
        setError('That password is not correct.');
      } else if (err instanceof ApiError && err.code === 'already_enabled') {
        setError('Two-factor authentication is already on for this account.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmStep() {
    const inputError = twoFactorInputError(code.trim());
    if (inputError) {
      setError(inputError);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const codes = await authService.confirmTwoFactor(code.trim());
      setRecoveryCodes(codes);
      setStep('recovery');
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'invalid_code'
          ? 'That code is wrong or has expired. Try the current code shown in your app.'
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleCopyAll() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      toast('Recovery codes copied');
    } catch {
      toast('Could not copy. You can still select the codes manually');
    }
  }

  function handleDownload() {
    downloadRecoveryCodes(recoveryCodes, accountHandle);
  }

  function handleDone() {
    reset();
    onEnabled();
  }

  const titles: Record<EnableStep, string> = {
    password: 'Turn on two-factor authentication',
    qr: 'Scan the QR code',
    recovery: 'Save your recovery codes',
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={titles[step]}
      // Fix-round item 7: the recovery step shows the 10 one-time codes —
      // once. Escape/backdrop must not be able to discard them after 2FA is
      // already enabled server-side; the only exit is "I saved these".
      dismissable={!busy && step !== 'recovery'}
    >
      {step === 'password' && (
        <Fields>
          <TextField
            label="Current password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Confirm it's you"
          />
          <Hint>
            Turning on two-factor authentication requires your password, even while signed in.
            You&apos;ll need an authenticator app (like Google Authenticator or 1Password) on your
            phone.
          </Hint>
          {error && <ErrorText role="alert">{error}</ErrorText>}
          <Actions>
            <Button variant="ghost" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void handlePasswordStep()} disabled={busy}>
              {busy ? 'Checking…' : 'Continue'}
            </Button>
          </Actions>
        </Fields>
      )}

      {step === 'qr' && (
        <Fields>
          {accountHandle && <Hint>Setting up two-factor authentication for {accountHandle}</Hint>}
          <QrWrap>
            <QrImage
              alt="QR code for your authenticator app"
              src={`data:image/svg+xml;utf8,${encodeURIComponent(qrSvg)}`}
            />
          </QrWrap>
          <Hint>
            In Google Authenticator or another authenticator app, add an account by scanning this QR
            code or entering the setup key below. For manual setup, choose time-based codes.
          </Hint>
          <SecretRow>
            <SecretLabel>Setup key</SecretLabel>
            <SecretText>{secret}</SecretText>
          </SecretRow>
          <OtpauthLink href={otpauthUri}>Open in an authenticator app</OtpauthLink>
          <Hint>
            Enter the 6-digit code generated by the app below. The setup key is not a login or
            recovery code. Keep the key and QR code private.
          </Hint>
          <TextField
            label="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="000000"
            inputMode="numeric"
          />
          {error && <ErrorText role="alert">{error}</ErrorText>}
          <Actions>
            <Button variant="ghost" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void handleConfirmStep()} disabled={busy}>
              {busy ? 'Verifying…' : 'Verify'}
            </Button>
          </Actions>
        </Fields>
      )}

      {step === 'recovery' && (
        <Fields>
          <Icon name="key" size={20} />
          {accountHandle && <Hint>Recovery codes for {accountHandle}</Hint>}
          <Hint>
            Save these 10 recovery codes somewhere safe. Each one can be used once, in place of a
            code from your app, if you ever lose access to it.
          </Hint>
          <WarnHint>This is the only time these codes will be shown.</WarnHint>
          <RecoveryList data-testid="recovery-codes">
            {recoveryCodes.map((c) => (
              <li key={c}>
                <RecoveryCode>{c}</RecoveryCode>
              </li>
            ))}
          </RecoveryList>
          <Actions>
            <Button variant="secondary" onClick={() => void handleCopyAll()}>
              Copy all
            </Button>
            <Button
              variant="secondary"
              onClick={handleDownload}
              aria-label="Download recovery codes as a text file"
            >
              <Icon name="download" size={15} /> Download
            </Button>
            <Button variant="primary" onClick={handleDone}>
              I saved these
            </Button>
          </Actions>
        </Fields>
      )}
    </Dialog>
  );
}

// ── Disable ───────────────────────────────────────────────────────────────

export interface TwoFactorDisableDialogProps {
  open: boolean;
  onClose: () => void;
  onDisabled: () => void;
}

/** Password + code re-auth — same weight as adding it (RemoveContactDialog model). */
export function TwoFactorDisableDialog({ open, onClose, onDisabled }: TwoFactorDisableDialogProps) {
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function close() {
    setPassword('');
    setCode('');
    setError('');
    onClose();
  }

  async function handleDisable() {
    setBusy(true);
    setError('');
    try {
      await authService.disableTwoFactor(password, code.trim());
      toast('Two-factor authentication is off');
      close();
      onDisabled();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'wrong_password') {
        setError('That password is not correct.');
      } else if (err instanceof ApiError && err.code === 'invalid_code') {
        setError('That code is wrong. Use your authenticator app or a recovery code.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={close} title="Turn off two-factor authentication">
      <Fields>
        <WarnHint role="note">
          Turning this off removes your verified badge immediately, if you have one. A verified
          badge requires two-factor authentication.
        </WarnHint>
        <TextField
          label="Current password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Confirm it's you"
        />
        <TextField
          label="6-digit code or recovery code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="000000"
        />
        {error && <ErrorText role="alert">{error}</ErrorText>}
      </Fields>
      <Actions>
        <Button variant="ghost" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={() => void handleDisable()}
          disabled={busy || !password || !code.trim()}
        >
          {busy ? 'Turning off…' : 'Turn off'}
        </Button>
      </Actions>
    </Dialog>
  );
}
